import React, { useState, useMemo } from 'react';
import { X, Megaphone, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BRANDS = [
  { id: 'probound', label: 'ProBound', accent: 'text-[#2F5E96]', border: 'border-[#2F5E96]', bg: 'bg-[#2F5E96]/10' },
  { id: 'proepoxy', label: 'ProEpoxy', accent: 'text-[#7A0B7A]', border: 'border-[#7A0B7A]', bg: 'bg-[#7A0B7A]/10' },
  { id: 'prodecor', label: 'ProDecor', accent: 'text-[#D4A24B]', border: 'border-[#D4A24B]', bg: 'bg-[#D4A24B]/10' },
];

/**
 * Simple picker: choose which installers to broadcast to (by brand access)
 * and which channel. On "Continue" it calls `onPick({ brand_access, channel, matched })`
 * so the parent can open the compose modal in broadcast mode.
 *
 * Props:
 *   open, onClose, installers, onPick
 */
export default function BroadcastPicker({ open, onClose, installers, onPick }) {
  const [selectedBrands, setSelectedBrands] = useState(['probound']);
  const [channel, setChannel] = useState('sms');

  const matched = useMemo(() => {
    if (!installers) return [];
    if (selectedBrands.length === 0) return installers;
    return installers.filter((i) => selectedBrands.some((b) => i.access?.[b]));
  }, [installers, selectedBrands]);

  const withPhone = matched.filter((i) => (i.phone || '').trim().length > 0);
  const withoutPhone = matched.length - withPhone.length;

  const toggle = (b) => {
    setSelectedBrands((prev) => prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]);
  };

  if (!open) return null;

  const brandLabels = BRANDS.filter((b) => selectedBrands.includes(b.id)).map((b) => b.label).join(' + ') || 'all installers';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" data-testid="broadcast-picker">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-amber-600" /> Broadcast
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Send one message to a group of installers</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Send to installers with access to</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {BRANDS.map((b) => {
                const on = selectedBrands.includes(b.id);
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => toggle(b.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors ${on ? `${b.border} ${b.bg} ${b.accent}` : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                    data-testid={`broadcast-brand-${b.id}`}
                  >
                    {b.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-gray-500 mt-2">Leave all off to target every installer.</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Channel</label>
            <div className="mt-2 inline-flex rounded-full bg-gray-100 p-0.5">
              <button
                type="button"
                onClick={() => setChannel('sms')}
                className={`px-3 py-1 rounded-full text-sm font-semibold ${channel === 'sms' ? 'bg-blue-600 text-white' : 'text-blue-700 hover:bg-blue-50'}`}
                data-testid="broadcast-channel-sms"
              >
                SMS Text
              </button>
              <button
                type="button"
                onClick={() => setChannel('whatsapp')}
                className={`px-3 py-1 rounded-full text-sm font-semibold ${channel === 'whatsapp' ? 'bg-emerald-600 text-white' : 'text-emerald-700 hover:bg-emerald-50'}`}
                data-testid="broadcast-channel-whatsapp"
              >
                WhatsApp
              </button>
            </div>
          </div>

          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
            <Users className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Matches <b>{matched.length} installers</b> · {withPhone.length} will receive
              {withoutPhone > 0 ? ` · ${withoutPhone} skipped (no phone on file)` : ''}
            </span>
          </div>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            size="sm"
            onClick={() => onPick({
              brand_access: selectedBrands,
              channel,
              matched: withPhone,
              label: `${withPhone.length} installers · ${brandLabels}`,
            })}
            disabled={withPhone.length === 0}
            className={channel === 'whatsapp' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}
            data-testid="broadcast-continue"
          >
            Continue · {withPhone.length} recipient{withPhone.length === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </div>
  );
}
